import { useEffect, useRef, useState } from "react";

type LatLngish = { lat: number; lng: number } | null | undefined;

/**
 * Animates a map position between the last two known fixes.
 *
 * WHY: bus positions arrive every ~5 seconds, so a marker bound straight to the
 * document teleports — five seconds of nothing, then a jump. Gabriel, sitting in
 * an Uber watching its map (2026-08-02): "their map moves so cleanly, like there
 * is now wasy we can be like that?? ... they may not be important but they are
 * appealing to the eye hence more sales."
 *
 * The mobile app got this first; this is the same idea for the dashboard so the
 * two agree.
 *
 * IMPORTANT: this changes only what is DRAWN. The stored position, the trail,
 * the distance and every geofence still use the real fixes — a cosmetic smoother
 * that quietly altered the numbers would be a much worse bug than a jumpy pin.
 *
 * Falls back to the raw position when the browser prefers reduced motion, and
 * snaps rather than crawls when a fix arrives far away (a resumed trip after an
 * app kill should not spend a second sliding across Nairobi).
 */
export function useGlidingPosition(
  target: LatLngish,
  durationMs?: number,
): { lat: number; lng: number } | null {
  const [drawn, setDrawn] = useState<{ lat: number; lng: number } | null>(
    target ? { lat: target.lat, lng: target.lng } : null,
  );
  const fromRef = useRef<{ lat: number; lng: number } | null>(null);
  const startedRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  /* GLIDE FOR AS LONG AS THE GAP, NOT A FIXED 1.2 s.
   *
   * The old default animated over 1200 ms while fixes land about every 4.5 s.
   * So the marker slid for a second and then SAT STILL for three and a half —
   * motionless roughly three quarters of the time. Gabriel, 2026-08-09: "for the
   * animation, there is no way to make it smooth, basically no 5sec wait??"
   *
   * There is: measure how long the last gap actually was and spend the next
   * glide covering it. The marker is then always moving, because it is always
   * mid-animation when the next fix arrives. This is the same fix the mobile
   * maps got the same day (their cap was 5 s against 9-15 s gaps).
   *
   * Bounds are sanity rails, not tuning: below 800 ms it is a jump, above 20 s
   * the feed has stalled and crawling would be a lie about a bus that is not
   * moving. First fix of a session has no gap to measure, so it uses the floor. */
  const lastFixAtRef = useRef(0);
  const spanRef = useRef(1200);
  const MIN_GLIDE_MS = 800;
  const MAX_GLIDE_MS = 20000;

  useEffect(() => {
    if (!target) {
      setDrawn(null);
      return;
    }
    const to = { lat: target.lat, lng: target.lng };
    const from = fromRef.current;

    // Time since the previous DIFFERENT position — that is the window the next
    // glide has to fill. Held/duplicate positions never reach here (the effect
    // keys on lat/lng), so this measures real movement, not render churn.
    const nowMs = Date.now();
    if (lastFixAtRef.current > 0) {
      const gap = nowMs - lastFixAtRef.current;
      if (gap >= MIN_GLIDE_MS) {
        spanRef.current = Math.min(gap, MAX_GLIDE_MS);
      }
    }
    lastFixAtRef.current = nowMs;
    const glideMs = durationMs ?? spanRef.current;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // ~0.02° is roughly 2 km — a jump that big is a resume or a bad fix, not
    // motion worth animating.
    const tooFar =
      !!from &&
      (Math.abs(to.lat - from.lat) > 0.02 || Math.abs(to.lng - from.lng) > 0.02);

    if (!from || reduceMotion || tooFar) {
      fromRef.current = to;
      setDrawn(to);
      return;
    }

    const start = from;
    startedRef.current = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - startedRef.current) / glideMs);
      // easeOutCubic — quick off the mark, settles gently, which is what reads
      // as "smooth" rather than "floaty".
      const e = 1 - Math.pow(1 - t, 3);
      const next = {
        lat: start.lat + (to.lat - start.lat) * e,
        lng: start.lng + (to.lng - start.lng) * e,
      };
      setDrawn(next);
      fromRef.current = next;
      if (t < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
    // `glideMs` is derived inside the effect from refs, so it is not a dep.
  }, [target?.lat, target?.lng, durationMs]);

  return drawn;
}
